if (!customElements.get('featured-collection')) {
    customElements.define(
        'featured-collection',
        class FeaturedCollection extends HTMLElement {
            constructor() {
                super();
            }

            connectedCallback() {
                this.selectors = {
                    sliderWrapper: '.featured-collection__items',
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                    pagination: '.swiper-pagination',
                    autoplayToggle: '.featured-collection__autoplay-toggle',
                };
                this.classes = {
                    grid: 'f-grid',
                    swiper: 'swiper',
                    swiperWrapper: 'swiper-wrapper',
                };

                this.blockId = this.dataset.blockId;
                this.block = this.closest(`[data-id="${this.blockId}"`);
                this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

                this.enableSlider = this.dataset.enableSlider === 'true';
                this.items = parseInt(this.dataset.items);
                this.tabletItems = parseInt(this.dataset.tabletItems);
                this.laptopItems = parseInt(this.dataset.laptopItems);
                this.slidesPerMove = parseInt(this.dataset.slidesPerMove) || 1;
                this.paginationType = this.dataset.paginationType || 'bullets';
                this.autoplayEnabled = this.dataset.autoplayEnabled === 'true';
                this.autoplayDelay = parseInt(this.dataset.autoplayDelay) || 5000;
                this.autoplayToggle = this.block.querySelector(this.selectors.autoplayToggle);
                this.hasAutoplay = this.autoplayEnabled || Boolean(this.autoplayToggle);
                this.autoplayStoppedByUser = !this.autoplayEnabled;
                this.handleAutoplayToggle = this.toggleAutoplay.bind(this);

                this.sliderInstance = false;

                if (!this.enableSlider) return;

                if (this.autoplayToggle) {
                    this.autoplayToggle.addEventListener('click', this.handleAutoplayToggle);
                }

                const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
                mql.onchange = this.init.bind(this);
                this.init();
            }

            init() {
                if (FoxTheme.config.mqlMobile) {
                    this.destroySlider();
                } else {
                    this.initSlider();
                }
            }

            initSlider() {
                if (typeof this.sliderInstance === 'object') return;
                const columnGap = window
                    .getComputedStyle(this.sliderWrapper)
                    .getPropertyValue('--f-column-gap');
                const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;

                this.sliderOptions = {
                    slidesPerView: 2,
                    slidesPerGroup: this.slidesPerMove,
                    spaceBetween: spaceBetween,
                    navigation: {
                        nextEl: this.block.querySelector(this.selectors.nextEl),
                        prevEl: this.block.querySelector(this.selectors.prevEl),
                    },
                    pagination: {
                        el: this.block.querySelector(this.selectors.pagination),
                        type: this.paginationType,
                    },
                    breakpoints: {
                        768: {
                            slidesPerView: this.tabletItems,
                            slidesPerGroup: this.slidesPerMove,
                        },
                        1024: {
                            slidesPerView: this.laptopItems,
                            slidesPerGroup: this.slidesPerMove,
                        },
                        1280: {
                            slidesPerView: this.items,
                            slidesPerGroup: this.slidesPerMove,
                        },
                    },
                    loop: false,
                    rewind: false,
                    threshold: 2,
                    watchSlidesProgress: true,
                    mousewheel: {
                        enabled: true,
                        forceToAxis: true,
                    },
                    ...(this.hasAutoplay && {
                        autoplay: {
                            delay: this.autoplayDelay,
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true,
                        },
                    }),
                };

                this.classList.add(this.classes.swiper);
                this.sliderWrapper.classList.remove(this.classes.grid);
                this.sliderWrapper.classList.add(this.classes.swiperWrapper);

                const additionModules = [FoxTheme.Swiper.Mousewheel];
                if (this.hasAutoplay) {
                    additionModules.push(FoxTheme.Swiper.Autoplay);
                }

                this.sliderInstance = new window.FoxTheme.Carousel(
                    this,
                    this.sliderOptions,
                    additionModules
                );
                this.sliderInstance.init();

                if (this.hasAutoplay && !this.autoplayEnabled) {
                    this.sliderInstance.slider.autoplay.stop();
                }

                this.updateAutoplayToggle();

                // const focusableElements = FoxTheme.a11y.getFocusableElements(this);

                // focusableElements.forEach((element) => {
                //   element.addEventListener('focusin', () => {
                //     const slide = element.closest('.swiper-slide');
                //     this.sliderInstance && this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
                //   });
                // });

                this.calcNavButtonsPosition();
                this.fixQuickviewDuplicate();
            }

            disconnectedCallback() {
                if (this.autoplayToggle) {
                    this.autoplayToggle.removeEventListener('click', this.handleAutoplayToggle);
                }
            }

            toggleAutoplay() {
                const autoplay = this.sliderInstance?.slider?.autoplay;
                if (!autoplay) return;

                this.autoplayStoppedByUser = !this.autoplayStoppedByUser;
                if (this.autoplayStoppedByUser) {
                    autoplay.stop();
                } else {
                    autoplay.start();
                }
                this.updateAutoplayToggle();
            }

            updateAutoplayToggle() {
                if (!this.autoplayToggle) return;

                const playIcon = this.autoplayToggle.querySelector('[data-autoplay-play]');
                const pauseIcon = this.autoplayToggle.querySelector('[data-autoplay-pause]');
                const playLabel = this.autoplayToggle.dataset.playLabel;
                const pauseLabel = this.autoplayToggle.dataset.pauseLabel;

                this.autoplayToggle.setAttribute(
                    'aria-pressed',
                    this.autoplayStoppedByUser ? 'true' : 'false'
                );
                this.autoplayToggle.setAttribute(
                    'aria-label',
                    this.autoplayStoppedByUser ? playLabel : pauseLabel
                );
                playIcon.hidden = !this.autoplayStoppedByUser;
                pauseIcon.hidden = this.autoplayStoppedByUser;
            }

            destroySlider() {
                this.classList.remove(this.classes.swiper);
                this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
                this.sliderWrapper.classList.add(this.classes.grid);
                if (typeof this.sliderInstance !== 'object') return;
                this.sliderInstance.slider.destroy();
                this.sliderInstance = false;
            }

            fixQuickviewDuplicate() {
                let modalIds = [];
                Array.from(this.querySelectorAll('quick-view-modal')).forEach(modal => {
                    const modalID = modal.getAttribute('id');
                    if (modalIds.includes(modalID)) {
                        modal.remove();
                    } else {
                        modalIds.push(modalID);
                    }
                });
            }

            calcNavButtonsPosition() {
                if (this.dataset.navigationPosition !== 'center_image') return;

                const firstMedia = this.querySelector('.product-card__image-wrapper');
                const controls = this.block.querySelector('.swiper-controls');
                if (controls && firstMedia && firstMedia.clientHeight > 0) {
                    controls.style.setProperty(
                        '--swiper-navigation-top-offset',
                        parseInt(firstMedia.clientHeight) / 2 + 'px'
                    );
                }
            }
        }
    );
}
